import { useDispatch } from 'react-redux';

// Types
import { AppDispatch } from 'types/RootState.types';
import { PayLaterCategoryProps } from './PayLaterCategory.types';

// Config
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { setSelectedPaymentType } from '@redux/actions/payment/paymentActions';
import { scrollToElement } from '@utils/index';

// Components
import { BillingDetails } from '@components/add-payment-details/card-details/billing-details';
import PaymentOption from '@components/add-payment-details/payment-option';
import KlarnaButton from '@components/add-payment-details/klarna-button';
import ClearpayButton from '@components/add-payment-details/clearpay-button';
import Container from '@components/container';
import OrderTotals from '@components/order-totals';
import PartnerDiscountPaymentUnavailable from '@components/partner-discount-payment-unavailable';
import PaymentOptionUnavailable from '@components/payment-option-unavailable';
import TermsConditions from '@components/terms-conditions';

// Styles
import styles from './pay-later-category.scss';

const PayLaterCategory = ({
  clearpayEligible,
  desktopResolution = false,
  disabled = false,
  klarnaEligible,
  partnerDiscountEnabled = false,
  selectedPaymentType = '',
  showOrderTotals = false,
}: PayLaterCategoryProps) => {
  const dispatch: AppDispatch = useDispatch();

  const handleOtherPayLaterOptionClicked = (paymentType: string | undefined) => {
    if (!paymentType) return;

    const clickedPaymentOptionIsAlreadySelected = selectedPaymentType === paymentType;

    if (!clickedPaymentOptionIsAlreadySelected) {
      setTimeout(() => {
        dispatch(setSelectedPaymentType({ paymentType }));

        scrollToElement({ id: paymentType });
      }, 0);
    }
  };

  const renderClearpayContent = (
    <Container borderTop="black" padding="2">
      {clearpayEligible?.isEligible ? (
        <Container testId="expanded-clearpay">
          {partnerDiscountEnabled ? (
            <PartnerDiscountPaymentUnavailable />
          ) : (
            <>
              <Container borderBottom="black" marginBottom="2" paddingBottom="2">
                <BillingDetails disabled={disabled} />
              </Container>
              {showOrderTotals && <OrderTotals containerWide />}
              <ClearpayButton />
              <TermsConditions />
            </>
          )}
        </Container>
      ) : (
        <PaymentOptionUnavailable paymentType={paymentTypeConstants.CLEARPAY} reasons={clearpayEligible?.reasons} />
      )}
    </Container>
  );

  const renderKlarnaContent = (
    <Container borderTop="black" padding="2">
      {klarnaEligible?.isEligible ? (
        <Container testId="expanded-klarna">
          {partnerDiscountEnabled ? (
            <PartnerDiscountPaymentUnavailable />
          ) : (
            <>
              <Container borderBottom="black" marginBottom="2" paddingBottom="2">
                <BillingDetails disabled={disabled} />
              </Container>
              {showOrderTotals && <OrderTotals containerWide />}
              <KlarnaButton />
              <TermsConditions />
            </>
          )}
        </Container>
      ) : (
        <PaymentOptionUnavailable paymentType={paymentTypeConstants.KLARNA} reasons={klarnaEligible?.reasons} />
      )}
    </Container>
  );

  return (
    <>
      <Container marginTop="1" renderIf={klarnaEligible?.isEnabled}>
        <PaymentOption
          data-testid={`payment-option-${paymentTypeConstants.KLARNA}`}
          desktopResolution={desktopResolution}
          disabled={disabled}
          icons={[styles.klarnaIcon]}
          id={paymentTypeConstants.KLARNA}
          label="Klarna"
          name="paymentOption"
          onClick={handleOtherPayLaterOptionClicked}
          selected={selectedPaymentType === paymentTypeConstants.KLARNA}
        >
          {renderKlarnaContent}
        </PaymentOption>
      </Container>
      <Container marginTop="1" renderIf={clearpayEligible?.isEnabled}>
        <PaymentOption
          data-testid={`payment-option-${paymentTypeConstants.CLEARPAY}`}
          desktopResolution={desktopResolution}
          disabled={disabled}
          icons={[styles.clearpayIcon]}
          id={paymentTypeConstants.CLEARPAY}
          label="Clearpay"
          name="paymentOption"
          onClick={handleOtherPayLaterOptionClicked}
          selected={selectedPaymentType === paymentTypeConstants.CLEARPAY}
        >
          {renderClearpayContent}
        </PaymentOption>
      </Container>
    </>
  );
};

export default PayLaterCategory;
